import React, { useState, useEffect } from "react";
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  AreaChart,
  Area,
} from "recharts";
import { motion } from "framer-motion";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faChartLine,
  faChartPie,
  faChartBar,
  faClock,
  faExpand,
  faDownload,
  faFilter,
  faInfoCircle,
  faTimes,
  faArrowUp,
  faArrowDown,
  faEquals,
} from "@fortawesome/free-solid-svg-icons";

const ActivityCharts = ({ metrics }) => {
  const [chartData, setChartData] = useState({
    timeline: [],
    typeDistribution: [],
    fieldActivity: [],
    hourlyActivity: [],
  });

  // State for expanded views
  const [expandedChart, setExpandedChart] = useState(null);

  // State for analytics
  const [analytics, setAnalytics] = useState({
    timeline: {
      peak: { time: "", value: 0 },
      average: 0,
      trend: "neutral",
      total: 0,
    },
    typeDistribution: {
      dominant: { type: "", percentage: 0 },
      total: 0,
    },
    fieldActivity: {
      mostActive: { field: "", count: 0 },
      totalFields: 0,
      totalChanges: 0,
    },
    hourlyActivity: {
      peakHour: { hour: "", count: 0 },
      quietHour: { hour: "", count: 0 },
      averagePerHour: 0,
    },
  });

  // Colors for charts
  const COLORS = [
    "#1e40af",
    "#3b82f6",
    "#60a5fa",
    "#93c5fd",
    "#dbeafe",
    "#2563eb",
  ];

  useEffect(() => {
    if (!metrics) return;

    // Process timeline data (last 30 minutes)
    const timelineData = [];
    const now = new Date();
    let peakTime = { time: "", value: 0 };
    let totalChanges = 0;

    for (let i = 29; i >= 0; i--) {
      const time = new Date(now - i * 60000);
      const key = `${time.getHours()}:${String(time.getMinutes()).padStart(
        2,
        "0"
      )}`;
      const changes = metrics.changesPerMinute[key] || 0;

      timelineData.push({
        time: key,
        changes: changes,
      });

      totalChanges += changes;
      if (changes > peakTime.value) {
        peakTime = { time: key, value: changes };
      }
    }

    const avgChanges = totalChanges / 30;

    // Determine trend
    const firstHalf = timelineData
      .slice(0, 15)
      .reduce((sum, d) => sum + d.changes, 0);
    const secondHalf = timelineData
      .slice(15)
      .reduce((sum, d) => sum + d.changes, 0);
    let trend = "neutral";
    if (secondHalf > firstHalf * 1.1) trend = "increasing";
    else if (secondHalf < firstHalf * 0.9) trend = "decreasing";

    // Process type distribution
    const typeData = Object.entries(metrics.changesByType || {}).map(
      ([type, count]) => ({
        name: type.charAt(0).toUpperCase() + type.slice(1),
        value: count,
      })
    );

    const totalTypeChanges = typeData.reduce((sum, d) => sum + d.value, 0);
    const dominantType = typeData.reduce(
      (max, d) => (d.value > (max?.value || 0) ? d : max),
      null
    );

    // Process field activity for bar chart
    const fieldData = Object.entries(metrics.changesByField || {})
      .sort((a, b) => b[1] - a[1])
      .slice(0, 10)
      .map(([field, count]) => ({
        field: field.replace(/([A-Z])/g, " $1").trim(),
        count,
        originalField: field,
      }));

    const totalFieldChanges = Object.values(
      metrics.changesByField || {}
    ).reduce((sum, count) => sum + count, 0);

    // Process hourly activity pattern
    const hourlyData = [];
    let peakHour = { hour: "", count: 0 };
    let quietHour = { hour: "", count: Number.MAX_VALUE };
    let totalHourlyActivity = 0;

    for (let hour = 0; hour < 24; hour++) {
      let hourTotal = 0;
      for (let minute = 0; minute < 60; minute++) {
        const key = `${hour}:${minute}`;
        hourTotal += metrics.changesPerMinute[key] || 0;
      }

      const hourKey = `${hour}:00`;
      hourlyData.push({
        hour: hourKey,
        activity: hourTotal,
      });

      totalHourlyActivity += hourTotal;

      if (hourTotal > peakHour.count) {
        peakHour = { hour: hourKey, count: hourTotal };
      }
      if (hourTotal < quietHour.count && hourTotal > 0) {
        quietHour = { hour: hourKey, count: hourTotal };
      }
    }

    const avgPerHour = totalHourlyActivity / 24;

    // Update analytics
    setAnalytics({
      timeline: {
        peak: peakTime,
        average: avgChanges.toFixed(1),
        trend: trend,
        total: totalChanges,
      },
      typeDistribution: {
        dominant: dominantType
          ? {
              type: dominantType.name,
              percentage: (
                (dominantType.value / totalTypeChanges) *
                100
              ).toFixed(1),
            }
          : { type: "None", percentage: 0 },
        total: totalTypeChanges,
      },
      fieldActivity: {
        mostActive: fieldData[0] || { field: "None", count: 0 },
        totalFields: Object.keys(metrics.changesByField || {}).length,
        totalChanges: totalFieldChanges,
      },
      hourlyActivity: {
        peakHour: peakHour,
        quietHour:
          quietHour.count === Number.MAX_VALUE
            ? { hour: "None", count: 0 }
            : quietHour,
        averagePerHour: avgPerHour.toFixed(1),
      },
    });

    setChartData({
      timeline: timelineData,
      typeDistribution: typeData,
      fieldActivity: fieldData,
      hourlyActivity: hourlyData,
    });
  }, [metrics]);

  // Custom tooltip for timeline
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white p-2 border border-gray-200 rounded shadow-lg">
          <p className="text-sm font-medium">{label}</p>
          <p className="text-sm text-blue-600">{payload[0].value} changes</p>
        </div>
      );
    }
    return null;
  };

  // Get trend icon
  const getTrendIcon = (trend) => {
    if (trend === "increasing") return faArrowUp;
    if (trend === "decreasing") return faArrowDown;
    return faEquals;
  };

  // Get trend color
  const getTrendColor = (trend) => {
    if (trend === "increasing") return "text-green-600";
    if (trend === "decreasing") return "text-red-600";
    return "text-gray-600";
  };

  // Render expanded modal
  const renderExpandedModal = () => {
    if (!expandedChart) return null;

    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50"
        onClick={() => setExpandedChart(null)}
      >
        <motion.div
          initial={{ scale: 0.9, y: 20 }}
          animate={{ scale: 1, y: 0 }}
          className="bg-white rounded-lg shadow-xl w-11/12 h-5/6 max-w-7xl overflow-hidden"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Modal Header */}
          <div className="flex justify-between items-center p-4 border-b border-gray-200 bg-gray-50">
            <h3 className="text-lg font-semibold text-gray-800">
              {expandedChart === "timeline" && "Activity Timeline Analysis"}
              {expandedChart === "typeDistribution" &&
                "Change Type Distribution Analysis"}
              {expandedChart === "fieldActivity" && "Field Activity Analysis"}
              {expandedChart === "hourlyActivity" &&
                "24-Hour Activity Pattern Analysis"}
            </h3>
            <button
              className="text-gray-500 hover:text-gray-700"
              onClick={() => setExpandedChart(null)}
            >
              <FontAwesomeIcon icon={faTimes} className="text-xl" />
            </button>
          </div>

          {/* Modal Content */}
          <div className="p-6 overflow-y-auto h-full pb-24">
            {expandedChart === "timeline" && renderExpandedTimeline()}
            {expandedChart === "typeDistribution" &&
              renderExpandedTypeDistribution()}
            {expandedChart === "fieldActivity" && renderExpandedFieldActivity()}
            {expandedChart === "hourlyActivity" &&
              renderExpandedHourlyActivity()}
          </div>
        </motion.div>
      </motion.div>
    );
  };

  // Render expanded timeline view
  const renderExpandedTimeline = () => (
    <>
      {/* Analytics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <h4 className="text-sm font-medium text-gray-700 mb-2">
            Peak Activity
          </h4>
          <p className="text-2xl font-bold text-blue-700">
            {analytics.timeline.peak.value}
          </p>
          <p className="text-xs text-gray-600">
            at {analytics.timeline.peak.time}
          </p>
        </div>
        <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-4">
          <h4 className="text-sm font-medium text-gray-700 mb-2">
            Average Rate
          </h4>
          <p className="text-2xl font-bold text-indigo-700">
            {analytics.timeline.average}
          </p>
          <p className="text-xs text-gray-600">changes per minute</p>
        </div>
        <div className="bg-sky-50 border border-sky-200 rounded-lg p-4">
          <h4 className="text-sm font-medium text-gray-700 mb-2">
            Total Changes
          </h4>
          <p className="text-2xl font-bold text-sky-700">
            {analytics.timeline.total}
          </p>
          <p className="text-xs text-gray-600">in last 30 minutes</p>
        </div>
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
          <h4 className="text-sm font-medium text-gray-700 mb-2">Trend</h4>
          <div className="flex items-center">
            <FontAwesomeIcon
              icon={getTrendIcon(analytics.timeline.trend)}
              className={`text-2xl mr-2 ${getTrendColor(
                analytics.timeline.trend
              )}`}
            />
            <p className="text-lg font-bold capitalize">
              {analytics.timeline.trend}
            </p>
          </div>
        </div>
      </div>

      {/* Large Chart */}
      <div
        className="bg-white border border-gray-200 rounded-lg p-4 mb-6"
        style={{ height: "400px" }}
      >
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={chartData.timeline}>
            <defs>
              <linearGradient
                id="colorChangesExpanded"
                x1="0"
                y1="0"
                x2="0"
                y2="1"
              >
                <stop offset="5%" stopColor="#2563eb" stopOpacity={0.8} />
                <stop offset="95%" stopColor="#2563eb" stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
            <XAxis dataKey="time" tick={{ fontSize: 12 }} />
            <YAxis tick={{ fontSize: 12 }} />
            <Tooltip content={<CustomTooltip />} />
            <Area
              type="monotone"
              dataKey="changes"
              stroke="#2563eb"
              strokeWidth={2}
              fillOpacity={1}
              fill="url(#colorChangesExpanded)"
              animationDuration={300}
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>

      {/* Detailed Table */}
      <div>
        <h4 className="text-sm font-medium text-gray-700 mb-3">
          Minute-by-Minute Breakdown
        </h4>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 border border-gray-200 rounded-lg">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                  Time
                </th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                  Changes
                </th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                  Status
                </th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                  Variance from Avg
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {chartData.timeline.map((item, index) => {
                const variance = (
                  ((item.changes - analytics.timeline.average) /
                    analytics.timeline.average) *
                  100
                ).toFixed(1);
                const status =
                  item.changes > analytics.timeline.average * 1.5
                    ? "High"
                    : item.changes < analytics.timeline.average * 0.5
                    ? "Low"
                    : "Normal";
                const statusColor =
                  status === "High"
                    ? "text-red-600 bg-red-50"
                    : status === "Low"
                    ? "text-yellow-600 bg-yellow-50"
                    : "text-green-600 bg-green-50";

                return (
                  <tr
                    key={index}
                    className={index % 2 === 0 ? "bg-white" : "bg-gray-50"}
                  >
                    <td className="px-4 py-2 text-sm">{item.time}</td>
                    <td className="px-4 py-2 text-sm font-medium">
                      {item.changes}
                    </td>
                    <td className="px-4 py-2 text-sm">
                      <span
                        className={`px-2 py-1 rounded-full text-xs ${statusColor}`}
                      >
                        {status}
                      </span>
                    </td>
                    <td className="px-4 py-2 text-sm">
                      {variance > 0 ? "+" : ""}
                      {variance}%
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );

  // Render expanded type distribution view
  const renderExpandedTypeDistribution = () => (
    <>
      {/* Analytics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <h4 className="text-sm font-medium text-gray-700 mb-2">
            Dominant Type
          </h4>
          <p className="text-xl font-bold text-blue-700">
            {analytics.typeDistribution.dominant.type}
          </p>
          <p className="text-sm text-gray-600">
            {analytics.typeDistribution.dominant.percentage}% of all changes
          </p>
        </div>
        <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-4">
          <h4 className="text-sm font-medium text-gray-700 mb-2">
            Total Changes
          </h4>
          <p className="text-2xl font-bold text-indigo-700">
            {analytics.typeDistribution.total}
          </p>
          <p className="text-xs text-gray-600">across all types</p>
        </div>
        <div className="bg-sky-50 border border-sky-200 rounded-lg p-4">
          <h4 className="text-sm font-medium text-gray-700 mb-2">Type Count</h4>
          <p className="text-2xl font-bold text-sky-700">
            {chartData.typeDistribution.length}
          </p>
          <p className="text-xs text-gray-600">different change types</p>
        </div>
      </div>

      {/* Charts Side by Side */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        <div
          className="bg-white border border-gray-200 rounded-lg p-4"
          style={{ height: "400px" }}
        >
          <h4 className="text-sm font-medium text-gray-700 mb-3">
            Distribution Chart
          </h4>
          <ResponsiveContainer width="100%" height="90%">
            <PieChart>
              <Pie
                data={chartData.typeDistribution}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={({ name, percent }) =>
                  `${name} ${(percent * 100).toFixed(0)}%`
                }
                outerRadius={120}
                fill="#2563eb"
                dataKey="value"
                animationBegin={0}
                animationDuration={800}
              >
                {chartData.typeDistribution.map((entry, index) => (
                  <Cell
                    key={`cell-${index}`}
                    fill={COLORS[index % COLORS.length]}
                  />
                ))}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
        </div>

        <div
          className="bg-white border border-gray-200 rounded-lg p-4"
          style={{ height: "400px" }}
        >
          <h4 className="text-sm font-medium text-gray-700 mb-3">
            Comparison View
          </h4>
          <ResponsiveContainer width="100%" height="90%">
            <BarChart data={chartData.typeDistribution}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="name" tick={{ fontSize: 12 }} />
              <YAxis tick={{ fontSize: 12 }} />
              <Tooltip />
              <Bar dataKey="value" fill="#3b82f6" animationDuration={800}>
                {chartData.typeDistribution.map((entry, index) => (
                  <Cell
                    key={`cell-${index}`}
                    fill={COLORS[index % COLORS.length]}
                  />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Type Details Table */}
      <div>
        <h4 className="text-sm font-medium text-gray-700 mb-3">
          Type Breakdown
        </h4>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 border border-gray-200 rounded-lg">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                  Type
                </th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                  Count
                </th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                  Percentage
                </th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                  Visual
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {chartData.typeDistribution.map((item, index) => {
                const percentage = (
                  (item.value / analytics.typeDistribution.total) *
                  100
                ).toFixed(1);

                return (
                  <tr
                    key={index}
                    className={index % 2 === 0 ? "bg-white" : "bg-gray-50"}
                  >
                    <td className="px-4 py-2 text-sm font-medium flex items-center">
                      <div
                        className="w-4 h-4 rounded mr-2"
                        style={{
                          backgroundColor: COLORS[index % COLORS.length],
                        }}
                      />
                      {item.name}
                    </td>
                    <td className="px-4 py-2 text-sm">{item.value}</td>
                    <td className="px-4 py-2 text-sm">{percentage}%</td>
                    <td className="px-4 py-2">
                      <div className="w-full bg-gray-200 rounded-full h-4">
                        <div
                          className="h-4 rounded-full"
                          style={{
                            width: `${percentage}%`,
                            backgroundColor: COLORS[index % COLORS.length],
                          }}
                        />
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );

  // Render expanded field activity view
  const renderExpandedFieldActivity = () => (
    <>
      {/* Analytics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className="bg-cyan-50 border border-cyan-200 rounded-lg p-4">
          <h4 className="text-sm font-medium text-gray-700 mb-2">
            Most Active Field
          </h4>
          <p className="text-lg font-bold text-cyan-700">
            {analytics.fieldActivity.mostActive.field}
          </p>
          <p className="text-sm text-gray-600">
            {analytics.fieldActivity.mostActive.count} changes
          </p>
        </div>
        <div className="bg-teal-50 border border-teal-200 rounded-lg p-4">
          <h4 className="text-sm font-medium text-gray-700 mb-2">
            Total Fields Modified
          </h4>
          <p className="text-2xl font-bold text-teal-700">
            {analytics.fieldActivity.totalFields}
          </p>
          <p className="text-xs text-gray-600">unique fields</p>
        </div>
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <h4 className="text-sm font-medium text-gray-700 mb-2">
            Total Field Changes
          </h4>
          <p className="text-2xl font-bold text-blue-700">
            {analytics.fieldActivity.totalChanges}
          </p>
          <p className="text-xs text-gray-600">across all fields</p>
        </div>
      </div>

      {/* Large Bar Chart */}
      <div
        className="bg-white border border-gray-200 rounded-lg p-4 mb-6"
        style={{ height: "400px" }}
      >
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={chartData.fieldActivity} layout="horizontal">
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
            <XAxis
              dataKey="field"
              tick={{ fontSize: 11 }}
              angle={-45}
              textAnchor="end"
              height={80}
            />
            <YAxis tick={{ fontSize: 12 }} />
            <Tooltip />
            <Bar dataKey="count" fill="#3b82f6" animationDuration={800}>
              {chartData.fieldActivity.map((entry, index) => (
                <Cell
                  key={`cell-${index}`}
                  fill={COLORS[index % COLORS.length]}
                />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Field Activity Table */}
      <div>
        <h4 className="text-sm font-medium text-gray-700 mb-3">
          Field Activity Details
        </h4>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 border border-gray-200 rounded-lg">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                  Rank
                </th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                  Field Name
                </th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                  Changes
                </th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                  % of Total
                </th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                  Activity Level
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {chartData.fieldActivity.map((item, index) => {
                const percentage = (
                  (item.count / analytics.fieldActivity.totalChanges) *
                  100
                ).toFixed(1);
                const activityLevel =
                  item.count > analytics.fieldActivity.totalChanges / 10
                    ? "High"
                    : item.count > analytics.fieldActivity.totalChanges / 20
                    ? "Medium"
                    : "Low";
                const levelColor =
                  activityLevel === "High"
                    ? "text-red-600 bg-red-50"
                    : activityLevel === "Medium"
                    ? "text-yellow-600 bg-yellow-50"
                    : "text-green-600 bg-green-50";

                return (
                  <tr
                    key={index}
                    className={index % 2 === 0 ? "bg-white" : "bg-gray-50"}
                  >
                    <td className="px-4 py-2 text-sm font-medium">
                      #{index + 1}
                    </td>
                    <td className="px-4 py-2 text-sm flex items-center">
                      <div
                        className="w-3 h-3 rounded-full mr-2"
                        style={{
                          backgroundColor: COLORS[index % COLORS.length],
                        }}
                      />
                      {item.field}
                    </td>
                    <td className="px-4 py-2 text-sm font-medium">
                      {item.count}
                    </td>
                    <td className="px-4 py-2 text-sm">{percentage}%</td>
                    <td className="px-4 py-2 text-sm">
                      <span
                        className={`px-2 py-1 rounded-full text-xs ${levelColor}`}
                      >
                        {activityLevel}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Additional Insights */}
      <div className="mt-6 bg-gray-50 p-4 rounded-lg">
        <h4 className="font-medium text-gray-700 mb-2">
          Field Activity Insights
        </h4>
        <ul className="space-y-2 text-sm text-gray-600">
          <li className="flex items-start">
            <FontAwesomeIcon
              icon={faInfoCircle}
              className="mr-2 mt-0.5 text-blue-500"
            />
            <span>
              The top 3 fields account for{" "}
              {(
                (chartData.fieldActivity
                  .slice(0, 3)
                  .reduce((sum, f) => sum + f.count, 0) /
                  analytics.fieldActivity.totalChanges) *
                100
              ).toFixed(1)}
              % of all field changes.
            </span>
          </li>
          <li className="flex items-start">
            <FontAwesomeIcon
              icon={faInfoCircle}
              className="mr-2 mt-0.5 text-blue-500"
            />
            <span>
              Average changes per field:{" "}
              {(
                analytics.fieldActivity.totalChanges /
                analytics.fieldActivity.totalFields
              ).toFixed(1)}
            </span>
          </li>
        </ul>
      </div>
    </>
  );

  // Render expanded hourly activity view
  const renderExpandedHourlyActivity = () => (
    <>
      {/* Analytics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
          <h4 className="text-sm font-medium text-gray-700 mb-2">Peak Hour</h4>
          <p className="text-xl font-bold text-orange-700">
            {analytics.hourlyActivity.peakHour.hour}
          </p>
          <p className="text-sm text-gray-600">
            {analytics.hourlyActivity.peakHour.count} changes
          </p>
        </div>
        <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
          <h4 className="text-sm font-medium text-gray-700 mb-2">Quiet Hour</h4>
          <p className="text-xl font-bold text-purple-700">
            {analytics.hourlyActivity.quietHour.hour}
          </p>
          <p className="text-sm text-gray-600">
            {analytics.hourlyActivity.quietHour.count} changes
          </p>
        </div>
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <h4 className="text-sm font-medium text-gray-700 mb-2">
            Average per Hour
          </h4>
          <p className="text-2xl font-bold text-blue-700">
            {analytics.hourlyActivity.averagePerHour}
          </p>
          <p className="text-xs text-gray-600">changes</p>
        </div>
        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
          <h4 className="text-sm font-medium text-gray-700 mb-2">
            Activity Ratio
          </h4>
          <p className="text-2xl font-bold text-green-700">
            {analytics.hourlyActivity.peakHour.count &&
            analytics.hourlyActivity.quietHour.count > 0
              ? (
                  analytics.hourlyActivity.peakHour.count /
                  analytics.hourlyActivity.quietHour.count
                ).toFixed(1)
              : "N/A"}
            x
          </p>
          <p className="text-xs text-gray-600">peak vs quiet</p>
        </div>
      </div>

      {/* Large Line Chart */}
      <div
        className="bg-white border border-gray-200 rounded-lg p-4 mb-6"
        style={{ height: "400px" }}
      >
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={chartData.hourlyActivity}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
            <XAxis dataKey="hour" tick={{ fontSize: 11 }} />
            <YAxis tick={{ fontSize: 12 }} />
            <Tooltip />
            <Line
              type="monotone"
              dataKey="activity"
              stroke="#1e40af"
              strokeWidth={3}
              dot={{ r: 4 }}
              animationDuration={1000}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>

      {/* Hourly Breakdown Table */}
      <div>
        <h4 className="text-sm font-medium text-gray-700 mb-3">
          24-Hour Breakdown
        </h4>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 border border-gray-200 rounded-lg">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                  Hour
                </th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                  Activity
                </th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                  Level
                </th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                  % of Daily
                </th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                  Visual
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {chartData.hourlyActivity.map((item, index) => {
                const totalDaily = chartData.hourlyActivity.reduce(
                  (sum, h) => sum + h.activity,
                  0
                );
                const percentage =
                  totalDaily > 0
                    ? ((item.activity / totalDaily) * 100).toFixed(1)
                    : "0";
                const level =
                  item.activity > analytics.hourlyActivity.averagePerHour * 1.5
                    ? "High"
                    : item.activity <
                      analytics.hourlyActivity.averagePerHour * 0.5
                    ? "Low"
                    : "Normal";
                const levelColor =
                  level === "High"
                    ? "text-red-600 bg-red-50"
                    : level === "Low"
                    ? "text-blue-600 bg-blue-50"
                    : "text-green-600 bg-green-50";

                return (
                  <tr
                    key={index}
                    className={index % 2 === 0 ? "bg-white" : "bg-gray-50"}
                  >
                    <td className="px-4 py-2 text-sm font-medium">
                      {item.hour}
                    </td>
                    <td className="px-4 py-2 text-sm">{item.activity}</td>
                    <td className="px-4 py-2 text-sm">
                      <span
                        className={`px-2 py-1 rounded-full text-xs ${levelColor}`}
                      >
                        {level}
                      </span>
                    </td>
                    <td className="px-4 py-2 text-sm">{percentage}%</td>
                    <td className="px-4 py-2">
                      <div className="w-full bg-gray-200 rounded-full h-3">
                        <div
                          className="h-3 rounded-full bg-blue-600"
                          style={{ width: `${percentage}%` }}
                        />
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pattern Analysis */}
      <div className="mt-6 bg-gray-50 p-4 rounded-lg">
        <h4 className="font-medium text-gray-700 mb-2">
          Activity Pattern Analysis
        </h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-gray-600">
          <div>
            <p className="font-medium mb-2">Business Hours (9:00-17:00)</p>
            <p>
              Activity:{" "}
              {chartData.hourlyActivity
                .filter((h, i) => i >= 9 && i <= 17)
                .reduce((sum, h) => sum + h.activity, 0)}{" "}
              changes
            </p>
          </div>
          <div>
            <p className="font-medium mb-2">After Hours</p>
            <p>
              Activity:{" "}
              {chartData.hourlyActivity
                .filter((h, i) => i < 9 || i > 17)
                .reduce((sum, h) => sum + h.activity, 0)}{" "}
              changes
            </p>
          </div>
        </div>
      </div>
    </>
  );

  return (
    <>
      <div className="space-y-6">
        {/* Real-time Activity Timeline */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="bg-white rounded-lg shadow p-4"
        >
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-sm font-medium text-gray-700 flex items-center">
              <FontAwesomeIcon
                icon={faChartLine}
                className="mr-2 text-blue-500"
              />
              Activity Timeline (Last 30 Minutes)
            </h3>
            <div className="flex space-x-1">
              <button
                title="Download chart"
                className="p-1 text-gray-400 hover:text-blue-600 transition-colors"
              >
                <FontAwesomeIcon icon={faDownload} />
              </button>
              <button
                title="Expand chart"
                className="p-1 text-gray-400 hover:text-blue-600 transition-colors"
                onClick={() => setExpandedChart("timeline")}
              >
                <FontAwesomeIcon icon={faExpand} />
              </button>
              <button
                title="Filter data"
                className="p-1 text-gray-400 hover:text-blue-600 transition-colors"
              >
                <FontAwesomeIcon icon={faFilter} />
              </button>
            </div>
          </div>
          <ResponsiveContainer width="100%" height={200}>
            <AreaChart data={chartData.timeline}>
              <defs>
                <linearGradient id="colorChanges" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#2563eb" stopOpacity={0.8} />
                  <stop offset="95%" stopColor="#2563eb" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="time" tick={{ fontSize: 12 }} interval={4} />
              <YAxis tick={{ fontSize: 12 }} />
              <Tooltip content={<CustomTooltip />} />
              <Area
                type="monotone"
                dataKey="changes"
                stroke="#2563eb"
                fillOpacity={1}
                fill="url(#colorChanges)"
                animationDuration={300}
              />
            </AreaChart>
          </ResponsiveContainer>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Change Type Distribution */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="bg-white rounded-lg shadow p-4"
          >
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-sm font-medium text-gray-700 flex items-center">
                <FontAwesomeIcon
                  icon={faChartPie}
                  className="mr-2 text-indigo-600"
                />
                Change Type Distribution
              </h3>
              <div className="flex space-x-1">
                <button
                  title="Download chart"
                  className="p-1 text-gray-400 hover:text-indigo-600 transition-colors"
                >
                  <FontAwesomeIcon icon={faDownload} />
                </button>
                <button
                  title="Expand chart"
                  className="p-1 text-gray-400 hover:text-indigo-600 transition-colors"
                  onClick={() => setExpandedChart("typeDistribution")}
                >
                  <FontAwesomeIcon icon={faExpand} />
                </button>
              </div>
            </div>
            <ResponsiveContainer width="100%" height={200}>
              <PieChart>
                <Pie
                  data={chartData.typeDistribution}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, percent }) =>
                    `${name} ${(percent * 100).toFixed(0)}%`
                  }
                  outerRadius={80}
                  fill="#2563eb"
                  dataKey="value"
                  animationBegin={0}
                  animationDuration={800}
                >
                  {chartData.typeDistribution.map((entry, index) => (
                    <Cell
                      key={`cell-${index}`}
                      fill={COLORS[index % COLORS.length]}
                    />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </motion.div>

          {/* Most Active Fields */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="bg-white rounded-lg shadow p-4"
          >
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-sm font-medium text-gray-700 flex items-center">
                <FontAwesomeIcon
                  icon={faChartBar}
                  className="mr-2 text-cyan-600"
                />
                Most Active Fields
              </h3>
              <div className="flex space-x-1">
                <button
                  title="Download chart"
                  className="p-1 text-gray-400 hover:text-cyan-600 transition-colors"
                >
                  <FontAwesomeIcon icon={faDownload} />
                </button>
                <button
                  title="Expand chart"
                  className="p-1 text-gray-400 hover:text-cyan-600 transition-colors"
                  onClick={() => setExpandedChart("fieldActivity")}
                >
                  <FontAwesomeIcon icon={faExpand} />
                </button>
              </div>
            </div>
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={chartData.fieldActivity} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis type="number" tick={{ fontSize: 12 }} />
                <YAxis
                  dataKey="field"
                  type="category"
                  tick={{ fontSize: 11 }}
                  width={80}
                />
                <Tooltip />
                <Bar dataKey="count" fill="#3b82f6" animationDuration={800}>
                  {chartData.fieldActivity.map((entry, index) => (
                    <Cell
                      key={`cell-${index}`}
                      fill={COLORS[index % COLORS.length]}
                    />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </motion.div>
        </div>

        {/* Hourly Activity Pattern */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.3 }}
          className="bg-white rounded-lg shadow p-4"
        >
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-sm font-medium text-gray-700 flex items-center">
              <FontAwesomeIcon icon={faClock} className="mr-2 text-sky-600" />
              24-Hour Activity Pattern
            </h3>
            <div className="flex space-x-1">
              <button
                title="Download chart"
                className="p-1 text-gray-400 hover:text-sky-600 transition-colors"
              >
                <FontAwesomeIcon icon={faDownload} />
              </button>
              <button
                title="Expand chart"
                className="p-1 text-gray-400 hover:text-sky-600 transition-colors"
                onClick={() => setExpandedChart("hourlyActivity")}
              >
                <FontAwesomeIcon icon={faExpand} />
              </button>
              <button
                title="Filter data"
                className="p-1 text-gray-400 hover:text-sky-600 transition-colors"
              >
                <FontAwesomeIcon icon={faFilter} />
              </button>
            </div>
          </div>
          <ResponsiveContainer width="100%" height={150}>
            <LineChart data={chartData.hourlyActivity}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="hour" tick={{ fontSize: 11 }} interval={2} />
              <YAxis tick={{ fontSize: 11 }} />
              <Tooltip />
              <Line
                type="monotone"
                dataKey="activity"
                stroke="#1e40af"
                strokeWidth={2}
                dot={{ r: 3 }}
                animationDuration={1000}
              />
            </LineChart>
          </ResponsiveContainer>
        </motion.div>
      </div>

      {/* Render the expanded modal */}
      {renderExpandedModal()}
    </>
  );
};

export default ActivityCharts;
